// redux-offline's `retryCount` belongs to the outbox rather than to an action, and peek can skip,
// so a parked write's failures land on the next sendable one. These counts are per action, and are
// two rather than one: `total` paces the back off, while `bounded` is the budget the discard bound
// measures. Only failures the application answered are charged to `bounded`, so a device that spent
// a shift offline does not come back with its budget already spent.
//
// They live on the action's own `meta` - redux-offline's client-side outbox envelope, alongside the
// `transaction` it stamps there itself - and never travel: offlineEffectOverride sends only the
// effect's path, body and headers. That is a different object from the record `meta` the server
// reads, which is a fixed whitelist in globalConfig and is not touched here.
//
// Keeping them there is what makes them survive a reload, and it is an in-place write outside a
// reducer, which needs justifying. redux-offline offers no way to patch an outbox item: peek hands
// back the live object out of `state.offline.outbox` and send passes that same object to discard
// and retry, so it is the only seam there is. It persists because redux-persist re-reads live state
// when it flushes (createPersistor.js:81), and because both writes happen in the same tick as the
// `scheduleRetry` dispatch that follows them, which replaces the `offline` slice and so queues that
// flush. A count with no retry behind it would not be worth persisting anyway.

const NO_ATTEMPTS = { total: 0, bounded: 0 };

function countsFor(action) {
    return action?.meta?.attempts ?? NO_ATTEMPTS;
}

/**
 * An action always has `meta` by the time it can fail - redux-offline stamps `transaction` on it as
 * it enqueues. Without one there is nowhere to keep a count, so the outbox counter stays in charge.
 */
function canCount(action) {
    return action?.meta != null;
}

function record(action, key) {
    if (!canCount(action)) return;
    const counts = countsFor(action);
    action.meta.attempts = { ...counts, [key]: counts[key] + 1 };
}

/**
 * Every retry scheduled for this action, whatever failed. Paces the back off, so an outage or a
 * long spell offline still widens the gap between attempts instead of hammering.
 *
 * @param {object} action the offline action being sent
 * @param {number} retriesFromOutbox redux-offline's outbox-wide count, used only as the fallback
 * @returns {number}
 */
export function totalAttemptsFor(action, retriesFromOutbox) {
    if (!canCount(action)) return retriesFromOutbox;
    return countsFor(action).total;
}

/**
 * Only the failures the application answered about - what the discard bound spends. An unbounded
 * failure (offline, a gateway, a deferred login) must not be able to exhaust it.
 */
export function boundedAttemptsFor(action, retriesFromOutbox) {
    if (!canCount(action)) return retriesFromOutbox;
    return countsFor(action).bounded;
}

/** Called where redux-offline increments its own counter: once a retry is actually scheduled. */
export function recordRetryScheduled(action) {
    record(action, 'total');
}

/** Called only when the application answered and the write is being kept for another attempt. */
export function recordBoundedFailure(action) {
    record(action, 'bounded');
}

export const _private = { countsFor };
